import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {computed, effect, inject, Injectable, PLATFORM_ID, signal} from '@angular/core';
import {AppLanguage, translations} from '../i18n/translations';

type InterpolationValue = string | number | boolean | null | undefined;
type InterpolationParams = Record<string, InterpolationValue>;
interface TranslationNode {
  [key: string]: string | TranslationNode;
}

@Injectable({
  providedIn: 'root'
})
export class I18nService {
  private static readonly STORAGE_KEY = 'crm-agentique.language';

  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT, {optional: true});

  private readonly _language = signal<AppLanguage>('en');
  readonly language = this._language.asReadonly();
  readonly localeTag = computed(() => {
    switch (this._language()) {
      case 'fr':
        return 'fr-FR';
      case 'it':
        return 'it-IT';
      case 'en':
      default:
        return 'en-US';
    }
  });

  constructor() {
    effect(() => {
      const language = this._language();
      const locale = this.localeTag();

      if (isPlatformBrowser(this.platformId)) {
        globalThis.localStorage.setItem(I18nService.STORAGE_KEY, language);
      }

      this.document?.documentElement.setAttribute('lang', locale);
    });
  }

  initialize(): void {
    this._language.set(this.resolveInitialLanguage());
  }

  setLanguage(language: AppLanguage): void {
    this._language.set(language);
  }

  coerceLanguage(language: string | null | undefined): AppLanguage | null {
    return language === 'en' || language === 'fr' || language === 'it' ? language : null;
  }

  t(key: string, params?: InterpolationParams): string {
    const template = this.lookupTranslation(key);
    if (typeof template !== 'string') {
      return key;
    }

    return this.interpolate(template, params);
  }

  tp(key: string, count: number, params?: InterpolationParams): string {
    const category = count === 1 ? 'one' : 'other';
    const template = this.lookupTranslation(`${key}.${category}`) ?? this.lookupTranslation(`${key}.other`);
    if (typeof template !== 'string') {
      return key;
    }

    return this.interpolate(template, {count, ...params});
  }

  translateEnum(prefix: string, value: string | null | undefined, fallbackKey: string): string {
    if (!value) {
      return this.t(fallbackKey);
    }

    const translation = this.lookupTranslation(`${prefix}.${value}`);
    if (typeof translation === 'string') {
      return translation;
    }

    return value
      .split('_')
      .map(fragment => fragment.charAt(0) + fragment.slice(1).toLowerCase())
      .join(' ');
  }

  formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
    return new Intl.NumberFormat(this.localeTag(), options).format(value);
  }

  private resolveInitialLanguage(): AppLanguage {
    if (!isPlatformBrowser(this.platformId)) {
      return 'en';
    }

    const storedLanguage = this.coerceLanguage(globalThis.localStorage.getItem(I18nService.STORAGE_KEY));
    if (storedLanguage) {
      return storedLanguage;
    }

    const browserLanguage = globalThis.navigator.language.toLowerCase();
    if (browserLanguage.startsWith('fr')) {
      return 'fr';
    }
    if (browserLanguage.startsWith('it')) {
      return 'it';
    }
    return 'en';
  }

  private lookupTranslation(key: string): string | TranslationNode | undefined {
    return this.lookupNode(translations[this._language()], key) ?? this.lookupNode(translations.en, key);
  }

  private lookupNode(root: TranslationNode, key: string): string | TranslationNode | undefined {
    const segments = key.split('.');
    let current: string | TranslationNode | undefined = root;

    for (const segment of segments) {
      if (current == null || typeof current === 'string') {
        return undefined;
      }
      current = current[segment];
    }

    return current;
  }

  private interpolate(template: string, params?: InterpolationParams): string {
    if (!params) {
      return template;
    }

    return template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key: string) => {
      const value = params[key];
      return value == null ? '' : String(value);
    });
  }
}
